import { formatMoney } from '@kit/shared/currency';

export const FREIGHT_ASSUMPTIONS = {
  /** IATA 1:6000 volumetric divisor expressed per cubic metre. */
  airVolumetricKgPerCbm: 167,
  /** Standard LCL weight/measure ratio: 1 CBM is billed as 1,000 kg. */
  seaRevenueTonKgPerCbm: 1000,
  /** Annual inventory carrying cost used for the speed comparison. */
  annualCarryingCostRate: 0.2,
  /** Beyond this, LCL often loses to a 20ft container. */
  fclCrossoverRevenueTons: 15,
} as const;

export type FreightInputs = {
  airFixedCharges: number;
  /** ISO 4217 code the quoted figures are in. Nothing is converted. */
  currency: string;
  airRatePerKg: number;
  airTransitDays: number;
  cargoValue: number | null;
  seaFixedCharges: number;
  seaMinimumRevenueTons: number;
  seaRatePerRevenueTon: number;
  seaTransitDays: number;
  volumeCbm: number;
  weightKg: number;
};

export type FreightModeEstimate = {
  /** Weight the carrier bills against, in kg. */
  chargeableKg: number;
  /** Total cost divided by actual gross weight. */
  costPerActualKg: number | null;
  /** Sea: total per W/M. Air: total per chargeable kg. */
  costPerChargeableUnit: number;
  fixedCharges: number;
  /** Variable freight before user-entered fixed charges. */
  mainFreight: number;
  /** Sea only, 0 for air. */
  revenueTons: number;
  totalCost: number;
  transitDays: { max: number; mid: number; min: number };
};

export type FreightEstimate = {
  air: FreightModeEstimate;
  /** How much more air costs than sea. Negative when air is cheaper. */
  airPremium: number;
  /** Cargo value at which carrying cost saved offsets the air premium. */
  breakEvenCargoValue: number | null;
  /** Carrying cost avoided by arriving earlier. Null without cargo value. */
  carryingCostSaved: number | null;
  cheaperMode: 'air' | 'sea';
  daysSaved: number;
  netAirAdvantage: number | null;
  recommendation: string;
  sea: FreightModeEstimate;
  warnings: string[];
};

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function parseNumericInput(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export function getFreightInputError(input: {
  volumeCbm: number;
  weightKg: number;
}): string | null {
  const weightKg = asNonNegative(input.weightKg);
  const volumeCbm = asNonNegative(input.volumeCbm);

  if (weightKg <= 0 && volumeCbm <= 0) {
    return 'Enter a gross weight or a volume to compare sea and air freight.';
  }

  return null;
}

function exactTransitDays(days: number) {
  return { max: days, mid: days, min: days };
}

export function calculateFreightCost(input: FreightInputs): FreightEstimate {
  const weightKg = asNonNegative(input.weightKg);
  const volumeCbm = asNonNegative(input.volumeCbm);
  const cargoValue =
    input.cargoValue === null ? null : asNonNegative(input.cargoValue);
  const seaRatePerRevenueTon = asNonNegative(input.seaRatePerRevenueTon);
  const seaFixedCharges = asNonNegative(input.seaFixedCharges);
  const seaMinimumRevenueTons = asNonNegative(input.seaMinimumRevenueTons);
  const airRatePerKg = asNonNegative(input.airRatePerKg);
  const airFixedCharges = asNonNegative(input.airFixedCharges);
  const seaTransitDays = asNonNegative(input.seaTransitDays);
  const airTransitDays = asNonNegative(input.airTransitDays);

  const measuredRevenueTons = Math.max(
    volumeCbm,
    weightKg / FREIGHT_ASSUMPTIONS.seaRevenueTonKgPerCbm,
  );
  const revenueTons = Math.max(measuredRevenueTons, seaMinimumRevenueTons);
  const seaVariableFreight = seaRatePerRevenueTon * revenueTons;
  const seaTotal = seaVariableFreight + seaFixedCharges;

  const volumetricKg = volumeCbm * FREIGHT_ASSUMPTIONS.airVolumetricKgPerCbm;
  const chargeableKg = Math.max(weightKg, volumetricKg);
  const airVariableFreight = chargeableKg * airRatePerKg;
  const airTotal = airVariableFreight + airFixedCharges;

  const sea: FreightModeEstimate = {
    chargeableKg: Math.round(
      revenueTons * FREIGHT_ASSUMPTIONS.seaRevenueTonKgPerCbm,
    ),
    costPerActualKg: weightKg > 0 ? roundCurrency(seaTotal / weightKg) : null,
    costPerChargeableUnit: roundCurrency(seaTotal / revenueTons),
    fixedCharges: roundCurrency(seaFixedCharges),
    mainFreight: roundCurrency(seaVariableFreight),
    revenueTons: Math.round(revenueTons * 100) / 100,
    totalCost: roundCurrency(seaTotal),
    transitDays: exactTransitDays(seaTransitDays),
  };

  const air: FreightModeEstimate = {
    chargeableKg: Math.round(chargeableKg),
    costPerActualKg: weightKg > 0 ? roundCurrency(airTotal / weightKg) : null,
    costPerChargeableUnit:
      chargeableKg > 0 ? roundCurrency(airTotal / chargeableKg) : 0,
    fixedCharges: roundCurrency(airFixedCharges),
    mainFreight: roundCurrency(airVariableFreight),
    revenueTons: 0,
    totalCost: roundCurrency(airTotal),
    transitDays: exactTransitDays(airTransitDays),
  };

  const airPremium = roundCurrency(airTotal - seaTotal);
  const daysSaved = Math.max(0, seaTransitDays - airTransitDays);
  const dailyCarryingRate = FREIGHT_ASSUMPTIONS.annualCarryingCostRate / 365;
  const carryingCostSaved =
    cargoValue !== null && cargoValue > 0
      ? roundCurrency(cargoValue * dailyCarryingRate * daysSaved)
      : null;
  const netAirAdvantage =
    carryingCostSaved === null
      ? null
      : roundCurrency(carryingCostSaved - airPremium);
  const breakEvenCargoValue =
    airPremium > 0 && daysSaved > 0
      ? roundCurrency(airPremium / (dailyCarryingRate * daysSaved))
      : null;

  const warnings: string[] = [];

  if (volumeCbm <= 0) {
    warnings.push(
      'No volume entered, so air is priced on gross weight alone. Real air quotes bill volumetric weight whenever the cargo is bulky.',
    );
  }
  if (weightKg <= 0) {
    warnings.push(
      'No weight entered, so the comparison is driven entirely by volume.',
    );
  }
  if (measuredRevenueTons < seaMinimumRevenueTons) {
    warnings.push(
      `This shipment falls under the ${seaMinimumRevenueTons} LCL W/M minimum you entered, so sea freight is priced at that minimum.`,
    );
  }
  if (revenueTons > FREIGHT_ASSUMPTIONS.fclCrossoverRevenueTons) {
    warnings.push(
      'Above roughly 15 CBM a full 20ft container may beat LCL. Ask your forwarder to quote FCL alongside this estimate.',
    );
  }
  warnings.push(
    'This calculation uses the rates and fixed charges you entered. Confirm what each quote includes before comparing totals, especially pickup, terminal handling, fuel, security, customs, delivery, insurance, duty, and tax.',
  );

  return {
    air,
    airPremium,
    breakEvenCargoValue,
    carryingCostSaved,
    cheaperMode: airTotal < seaTotal ? 'air' : 'sea',
    daysSaved,
    netAirAdvantage,
    recommendation: buildRecommendation({
      airPremium,
      carryingCostSaved,
      currency: input.currency,
      daysSaved,
      netAirAdvantage,
    }),
    sea,
    warnings,
  };
}

function buildRecommendation({
  airPremium,
  carryingCostSaved,
  currency,
  daysSaved,
  netAirAdvantage,
}: {
  airPremium: number;
  carryingCostSaved: number | null;
  currency: string;
  daysSaved: number;
  netAirAdvantage: number | null;
}) {
  const money = (value: number) =>
    formatMoney({ currency, decimals: 0, value });
  if (daysSaved <= 0) {
    if (airPremium <= 0) {
      return `Air is cheaper by ${money(Math.abs(airPremium))}, but it does not arrive sooner for the transit times entered. Confirm both quote scopes and schedules before booking.`;
    }

    return `Sea is cheaper by ${money(airPremium)}, and air does not arrive sooner for the transit times entered. Sea is the better choice on both cost and quoted transit time.`;
  }

  if (airPremium <= 0) {
    return `Air is the cheaper mode for these quote inputs and arrives about ${daysSaved} days sooner. Confirm that both quotes cover the same service scope before booking.`;
  }
  if (netAirAdvantage === null) {
    return `Sea is cheaper by ${money(airPremium)} and air arrives about ${daysSaved} days sooner. Add a cargo value to see whether the faster cash cycle covers that premium.`;
  }
  if (netAirAdvantage > 0) {
    return `Air pays for itself here: the ${daysSaved} days saved release about ${money(netAirAdvantage)} more in carrying cost than the ${money(airPremium)} premium costs you.`;
  }
  return `Sea is the better economic choice: the air premium of ${money(airPremium)} costs more than the ${money(carryingCostSaved ?? 0)} of carrying cost the ${daysSaved} saved days release. Pay for air only when the delivery date protects revenue.`;
}
